import React, { useState } from "react";
import { FiCheck, FiTrash2, FiPlus } from "react-icons/fi";

import SectionHeader from "../components/ui/SectionHeader";
import {
  useCreateTodoMutation,
  useDeleteTodoMutation,
  useGetTodosQuery,
  useUpdateTodoMutation,
} from "../services/api";
import { getApiErrorMessage } from "../utils/apiError";

const TodoPage = () => {
  const { data, isLoading } = useGetTodosQuery();
  const [createTodo] = useCreateTodoMutation();
  const [updateTodo] = useUpdateTodoMutation();
  const [deleteTodo] = useDeleteTodoMutation();

  const [input, setInput] = useState("");
  const [error, setError] = useState("");

  const todos = data?.data || [];
  const pending = todos.filter((t) => !t.done);
  const done    = todos.filter((t) =>  t.done);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setError("");
    try {
      await createTodo({ title: input.trim() }).unwrap();
      setInput("");
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to add todo."));
    }
  };

  const handleToggle = async (todo) => {
    try {
      await updateTodo({ id: todo.id, done: !todo.done }).unwrap();
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to update todo."));
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTodo(id).unwrap();
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to delete todo."));
    }
  };

  const TodoItem = ({ todo }) => (
    <div
      className="list-row"
      style={{
        opacity: todo.done ? 0.55 : 1,
        transition: "opacity 0.2s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flex: 1 }}>
        {/* Checkbox */}
        <button
          type="button"
          onClick={() => handleToggle(todo)}
          style={{
            width: 22,
            height: 22,
            borderRadius: 5,
            border: `2px solid ${todo.done ? "var(--success,#10b981)" : "var(--border,#d1d5db)"}`,
            background: todo.done ? "var(--success,#10b981)" : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            flexShrink: 0,
            padding: 0,
          }}
          aria-label={todo.done ? "Mark as pending" : "Mark as done"}
        >
          {todo.done && <FiCheck size={13} color="#fff" strokeWidth={3} />}
        </button>

        <span
          style={{
            textDecoration: todo.done ? "line-through" : "none",
            fontSize: "0.95rem",
          }}
        >
          {todo.title}
        </span>
      </div>

      <button
        type="button"
        className="table-action table-action--danger"
        onClick={() => handleDelete(todo.id)}
        aria-label="Delete todo"
        style={{ display: "flex", alignItems: "center", gap: 4 }}
      >
        <FiTrash2 size={13} />
        Delete
      </button>
    </div>
  );

  return (
    <div className="dashboard-page">
      <SectionHeader
        eyebrow="Todo List"
        title="Quick personal checklist — add tasks, check them off, keep moving."
      />

      <div className="dashboard-grid dashboard-grid--wide">
        {/* ── Add form ── */}
        <form
          className="panel form-panel form-modern"
          onSubmit={handleAdd}
          style={{ gridColumn: "span 2" }}
        >
          <div className="form-section">
            {error && (
              <div className="form-alert form-alert--danger">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
              <input
                className="form-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="What needs to be done?"
                style={{ flex: 1 }}
                autoFocus
              />
              <button className="btn-primary" type="submit" style={{ whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: "6px" }}>
                <FiPlus /> Add Item
              </button>
            </div>
          </div>
        </form>

        {/* ── Pending ── */}
        <section className="panel" style={{ gridColumn: "span 2" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.75rem",
            }}
          >
            <h3 style={{ margin: 0 }}>Pending</h3>
            <span
              style={{
                background: "var(--surface-alt,#f3f4f6)",
                borderRadius: 20,
                padding: "2px 10px",
                fontSize: "0.8rem",
                fontWeight: 600,
              }}
            >
              {pending.length}
            </span>
          </div>

          {isLoading && <p>Loading…</p>}

          {!isLoading && pending.length === 0 && (
            <p style={{ color: "var(--text-muted,#888)", fontSize: "0.9rem" }}>
              Nothing pending. Add something above.
            </p>
          )}

          {pending.map((todo) => (
            <TodoItem key={todo.id} todo={todo} />
          ))}
        </section>

        {/* ── Done ── */}
        {done.length > 0 && (
          <section className="panel" style={{ gridColumn: "span 2" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "0.75rem",
              }}
            >
              <h3 style={{ margin: 0, color: "var(--text-muted,#888)" }}>Completed</h3>
              <span
                style={{
                  background: "var(--surface-alt,#f3f4f6)",
                  borderRadius: 20,
                  padding: "2px 10px",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: "var(--text-muted,#888)",
                }}
              >
                {done.length}
              </span>
            </div>
            {done.map((todo) => (
              <TodoItem key={todo.id} todo={todo} />
            ))}
          </section>
        )}
      </div>
    </div>
  );
};

export default TodoPage;
